import { Fiche, FicheContainerContext } from '@mairistem/fiche';
import _ from 'lodash';
import React from 'react';
import { ModeReglementContextProvider, PayeurContextProvider } from '@/components/Context';
import { RegieContext } from '@/contexts';
import type { PayeurType } from '@/types';
import { payeurPanelValidators } from '@/utils/validators/fiche';
import { PayeurListe } from '../PayeurListe';
import PayeurPanelCommentaire from './Panel/PayeurPanelCommentaire';
import PayeurPanelFactures from './Panel/PayeurPanelFactures';
import PayeurPanelIdentification from './Panel/PayeurPanelIdentification';

import '../pocFiche.less';

interface PayeurFicheProps {
  loading?: boolean;
  errors?: { id: string; message: string }[];
  payeur?: PayeurType;
  onChange?: (data: any, shouldChangeStatus?: boolean) => void;
}

const PayeurFiche = (props: PayeurFicheProps) => {
  const { loading, errors, payeur, onChange } = props;

  const {
    identifiant,
    civilite,
    adresse,
    nom,
    prenom,
    dateNaissance,
    paysNaissance,
    villeNaissance,
    departementNaissance,
    mail,
    telephone,
    portable,
    logicielFacturier,
    commentaire,
    // Non renvoyés pas l'API mais utilisés dans le front :
    nouveauCommentaire,
  } = payeur || {};

  const { status, handleValidate } = React.useContext(FicheContainerContext);

  const { selectedRegie } = React.useContext(RegieContext);

  const handleChange = React.useCallback(
    (e, shouldChangeStatus: boolean = true) => {
      // const effectiveChangeStatus = isDataSettling.current ? false : shouldChangeStatus;
      // Si on a RAZ l'adresse
      if (
        e?.adresse &&
        _.isEmpty(e.adresse.numeroCodePostal) &&
        _.isEmpty(e.adresse.lettre) &&
        _.isEmpty(e.adresse.numeroVoirie) &&
        _.isEmpty(e.adresse.libellePays) &&
        _.isEmpty(e.adresse.libelleVoie)
      ) {
        onChange?.({ ...e, adresse: null }, shouldChangeStatus);
      } else {
        onChange?.(e, shouldChangeStatus);
      }
    },
    [onChange],
  );

  React.useEffect(
    () => {
      if (!_.isEmpty(errors) && handleValidate) {
        handleValidate();
      }
    },
    // Pas errors en dépendance, car on veut juste déclencher quand le payeur change
    // et pas quand error change, sinon boucle infini
    [payeur, handleValidate],
  );

  const identification = React.useMemo(
    () => (
      <Fiche.Panel
        key="identification"
        id="identification"
        title="IDENTIFICATION"
        open
        error={payeurPanelValidators.identificationHasErrors(errors)}
      >
        <PayeurPanelIdentification
          identifiant={identifiant}
          errors={errors}
          civilite={civilite}
          adresse={adresse}
          nom={nom}
          prenom={prenom}
          dateNaissance={dateNaissance}
          paysNaissance={paysNaissance}
          villeNaissance={villeNaissance}
          departementNaissance={departementNaissance}
          mail={mail}
          telephone={telephone}
          portable={portable}
          onChange={handleChange}
          disabled={!_.isNil(logicielFacturier)}
        />
      </Fiche.Panel>
    ),
    [
      identifiant,
      errors,
      adresse,
      civilite,
      dateNaissance,
      paysNaissance,
      departementNaissance,
      mail,
      nom,
      prenom,
      villeNaissance,
      telephone,
      portable,
      logicielFacturier,
      handleChange,
    ],
  );

  const factures = React.useMemo(
    () => (
      <Fiche.Panel
        key="factures"
        id="factures_recus"
        title="FACTURES ET REÇUS"
        open
        disabled={status?.created}
      >
        <PayeurPanelFactures />
      </Fiche.Panel>
    ),
    [status?.created],
  );

  const commentaires = React.useMemo(
    () => (
      <Fiche.Panel
        key="commentaires"
        id="commentaires"
        title="COMMENTAIRES INTERNES"
        open
        error={payeurPanelValidators.commentaireHasErrors(errors)}
      >
        <PayeurPanelCommentaire
          identifiant={identifiant}
          commentaire={commentaire}
          nouveauCommentaire={nouveauCommentaire}
          onChange={handleChange}
          errors={errors}
        />
      </Fiche.Panel>
    ),
    [errors, identifiant, commentaire, nouveauCommentaire, handleChange],
  );

  const header = React.useMemo(() => {
    if (status?.created && !status?.updated) {
      return 'Nouveau payeur';
    }
    return `${_.toUpper(nom || '')} ${prenom || ''}`;
  }, [status?.created, status?.updated, nom, prenom]);

  const list = React.useMemo(
    () => ({
      left: <PayeurListe />,
    }),
    [],
  );

  return (
    <PayeurContextProvider identifiantPayeur={identifiant}>
      <ModeReglementContextProvider autoload>
        <Fiche
          key="PayeurFiche"
          type="payeur"
          header={header}
          loading={loading || _.isNil(selectedRegie)}
          errors={errors}
          sidebars={list}
        >
          {identification}
          {factures}
          {commentaires}
        </Fiche>
      </ModeReglementContextProvider>
    </PayeurContextProvider>
  );
};

export default PayeurFiche;
